import { call, put, takeLatest } from 'redux-saga/effects';
import type { SagaIterator } from 'redux-saga';
import { HTTP_STATUS } from '@/app/config/constants';
import { createApiError, getErrorMessage, normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { ENTITY_ACCESS_ERROR_MESSAGE, isEntityInactiveSignIn } from '@/shared/services/api/entityInactive';
import { logAuthEvent } from '@/core/logging/auditLogger';
import { logger } from '@/core/logging/logger';
import { isAdminRoleName } from '@/core/security/roles';
import { tokenService } from '@/shared/services/auth/tokenService';
import { clearStoredTheme } from '@/shared/hooks/useTheme';
import { bootstrapOrganizationSession } from '@/features/organization-setup';
import { authService } from '../api/authService';
import { AUTH_MESSAGES, TOKEN_REFRESH_SKEW_MS } from '../constants';
import type { AuthTokens, OtpSendResponse, User, UserProfile } from '../models';
import { supplierFromToken, supplierUser } from '../utils/supplierSession';
import { fallbackUser, profileToUser, roleNameOf } from '../utils/userMapper';
import {
  hydrateAuth,
  loginFailure,
  loginStart,
  loginSuccess,
  logout,
  logoutStart,
  resendOtpFailure,
  resendOtpStart,
  resendOtpSuccess,
  sendOtpFailure,
  sendOtpStart,
  sendOtpSuccess,
  setHydrated,
  verifyOtpFailure,
  verifyOtpStart,
  verifyOtpSuccess,
} from './authSlice';

/** Step 2: persist tokens and flip `isAuth`. */
function persist(tokens: AuthTokens): number {
  tokenService.setTokens(tokens);
  return Date.now() + tokens.expiresIn * 1000;
}

/** Step 4: persist the five profile fields the document lists. */
function persistUser(user: User): void {
  tokenService.setUser({
    userId: user.id,
    userName: user.userName,
    firstName: user.firstName,
    lastName: user.lastName,
    emailAddress: user.emailAddress,
  });
}

/**
 * Turns the two windows into the absolute timestamps the screen counts down to.
 *
 * Both come from `POST /auth/otp/request`, which reports the server's own
 * `otp-ttl` and `otp-resend-interval`; authService has already substituted the
 * client defaults if a response omitted them, so both are real seconds here.
 */
function otpTimers(
  response: OtpSendResponse,
): { expiresAt: number; resendAvailableAt: number; ttlSeconds: number } {
  const now = Date.now();
  return {
    expiresAt: now + response.expiresIn * 1000,
    resendAvailableAt: now + response.resendAfter * 1000,
    ttlSeconds: response.expiresIn,
  };
}

/**
 * Is this failure the session itself being rejected, rather than the one call failing?
 *
 * Only a 401 counts. A 403 is an authorization refusal by a session that is still perfectly
 * valid -- treating it as an expiry is what used to throw a signed-in user back to the login
 * page for opening one page they were not entitled to.
 */
function isSessionExpiry(error: unknown): boolean {
  return normalizeError(error).status === HTTP_STATUS.UNAUTHORIZED;
}

/**
 * Tears the session down locally: no `POST /auth/logout`, because the credential the server
 * would authenticate that call with is the one it has just rejected.
 *
 * `logout()` leaves `hydrated` true, so ProtectedRoute redirects to /login on the next render
 * -- an in-app navigation, which is also why this cannot loop: /login is public, so nothing
 * there issues the request that would expire the session again.
 */
function* endSession(): SagaIterator {
  // Every key, so the wizard's stored organization code goes with the tokens -- otherwise the
  // next user to sign in on this browser resumes against the previous one's organization.
  tokenService.clearAll();
  // The organization theme goes with them: the next sign-in re-reads it from the organization.
  clearStoredTheme();
  yield put(logout());
  logAuthEvent('session.expired');
}

/**
 * Step 3 + step 4, shared by login and hydration.
 *
 * A failed profile fetch is NOT fatal: loginapi.md section 8 requires the user
 * to "proceed to dashboard with limited access", so the session survives with
 * a stand-in user and `profileLoaded: false`.
 *
 * With one exception -- a 401. The stand-in user carries no roles, so admitting one on an
 * expired token let the session pass ProtectedRoute's authentication check and then fail its
 * role check, landing the user on /unauthorized instead of the login page. A rejected token is
 * rethrown so the caller can end the session.
 */
function* resolveUser(identifier: string): SagaIterator<{ user: User; loaded: boolean }> {
  try {
    const profile: UserProfile = yield call(authService.getProfile, identifier);
    const user = profileToUser(profile);
    persistUser(user);

    /*
      organization-setup-step1-apiflow.md section 2: the same /users/me call
      that resolves the profile also carries organizationCode, which decides
      whether step 1 will create or update. Storing it here means the wizard
      never has to ask again, and never issues a second /users/me.

      The code is stored for *every* role. Only the follow-up read is skipped
      for a tenant Admin: that role has no Organization Setup screen, so the
      bootstrap's GET /organizations/{code} would be a request nothing goes on
      to use. The role name is read through the same helper the selectors use,
      so "which role is an Admin" stays in one place.

      This used to skip the whole call for an Admin, which meant the one role
      that lives in the Masters was the one role with no organization in the
      session -- so the Organization field on Add Business Unit, Add Division
      and Add Department had nothing to pre-fill from and sat empty.
    */
    yield call(bootstrapOrganizationSession, profile.organizationCode, {
      hydrate: !isAdminRoleName(roleNameOf(profile.role)),
    });

    return { user, loaded: true };
  } catch (error) {
    if (isSessionExpiry(error)) throw error;
    logger.warn('Profile fetch failed; continuing with limited access', error);
    logAuthEvent('hydrate.failure');
    return { user: fallbackUser(identifier), loaded: false };
  }
}

/* -- step 1: login --------------------------------------------------------- */

function* loginSaga(action: ReturnType<typeof loginStart>): SagaIterator {
  let minted = false;
  try {
    const tokens: AuthTokens = yield call(authService.login, action.payload);
    const expiresAt = persist(tokens);
    minted = true;

    const { user, loaded } = yield call(resolveUser, action.payload.username);

    yield put(
      loginSuccess({
        user,
        token: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        tokenType: tokens.tokenType,
        expiresAt,
        profileLoaded: loaded,
      }),
    );
    logAuthEvent('login.success');
    // LoginPage watches isAuthenticated and navigates to /dashboard (step 5).
  } catch (error) {
    /*
      A 401 here is one of two things, and they end differently. Wrong credentials are a refusal
      from the public sign-in endpoint: nothing was persisted and there is no session to end, so it
      is only reported -- ending one would reset the sign-in state around the form. A token minted a
      moment ago and rejected by the very next call is not a session worth keeping, so that one is
      torn down. Either way the user stays on the login page with the message.
    */
    if (minted && isSessionExpiry(error)) yield call(endSession);
    /* No Active entity to work in (2026-10-15): the Login page's "Access not available" alert, not the plain line. */
    yield put(
      loginFailure(
        isEntityInactiveSignIn(error) ? ENTITY_ACCESS_ERROR_MESSAGE : getErrorMessage(normalizeError(error)),
      ),
    );
    logAuthEvent('login.failure');
  }
}

/* -- step 7: logout -------------------------------------------------------- */

function* logoutSaga(): SagaIterator {
  // A failing logout must never block local cleanup (section 8).
  try {
    yield call(authService.logout);
  } catch (error) {
    logger.warn('Logout call failed; clearing locally anyway', error);
  }
  tokenService.clearAll();
  // Same boundary as `endSession`: the theme is the organization's, not this browser's.
  clearStoredTheme();
  /* Deliberate: nobody's destination is remembered for whoever signs in next -- see AuthState.signedOut. */
  yield put(logout({ deliberate: true }));
  logAuthEvent('logout');
  // `logout()` leaves `hydrated` true, so ProtectedRoute redirects to /login
  // on the next render -- in-app, no full reload.
}

/* -- session restore on reload --------------------------------------------- */

function* hydrateAuthSaga(): SagaIterator {
  try {
    const stored = tokenService.getTokens();
    const storedUser = tokenService.getUser();

    if (!stored.accessToken && !stored.refreshToken) return;

    const expired = tokenService.isAccessTokenExpired(TOKEN_REFRESH_SKEW_MS);
    let accessToken = stored.accessToken;
    let refreshToken = stored.refreshToken ?? undefined;
    let tokenType = stored.tokenType;
    let expiresAt = stored.tokenExpiry ?? null;

    // Refresh up front when the stored token has lapsed, so the first request
    // of the session does not have to 401 first.
    if ((!accessToken || expired) && stored.refreshToken) {
      const rotated: AuthTokens = yield call(authService.refresh, stored.refreshToken);
      expiresAt = persist(rotated);
      accessToken = rotated.accessToken;
      refreshToken = rotated.refreshToken;
      tokenType = rotated.tokenType;
    }

    if (!accessToken) {
      yield call(endSession);
      return;
    }

    /* A supplier's session (2026-10-23) is restored from its token: there is no profile to read for it. */
    const supplier = supplierFromToken(accessToken);
    if (supplier) {
      yield put(
        loginSuccess({
          user: supplierUser(supplier),
          token: accessToken,
          refreshToken,
          tokenType,
          ...(expiresAt !== null ? { expiresAt } : {}),
          profileLoaded: true,
        }),
      );
      logAuthEvent('hydrate.success');
      return;
    }

    const identifier = storedUser?.userName ?? '';
    const { user, loaded } = yield call(resolveUser, identifier);

    yield put(
      loginSuccess({
        user,
        token: accessToken,
        refreshToken,
        tokenType,
        ...(expiresAt !== null ? { expiresAt } : {}),
        profileLoaded: loaded,
      }),
    );
    logAuthEvent('hydrate.success');
  } catch {
    // A dead refresh token -- or a profile call the token no longer opens -- ends the session
    // (section 6). Nothing has rendered behind a guard yet, so this simply leaves the app
    // logged out and ProtectedRoute sends the first guarded route to /login.
    yield call(endSession);
    logAuthEvent('hydrate.failure');
  } finally {
    // Always -- otherwise ProtectedRoute never stops showing its spinner.
    yield put(setHydrated());
  }
}

/* -- OTP ------------------------------------------------------------------- */

function* sendOtpSaga(action: ReturnType<typeof sendOtpStart>): SagaIterator {
  try {
    /* The supplier portal's code goes to its own endpoint (2026-10-23); the staff sign-in's is unchanged. */
    const response: OtpSendResponse = yield call(
      action.payload.audience === 'supplier' ? authService.sendSupplierOtp : authService.sendOtp,
      action.payload.email,
    );
    yield put(sendOtpSuccess(otpTimers(response)));
    logAuthEvent('otp.sent');
  } catch (error) {
    yield put(sendOtpFailure(getErrorMessage(normalizeError(error))));
  }
}

function* resendOtpSaga(action: ReturnType<typeof resendOtpStart>): SagaIterator {
  try {
    /* Resend follows whoever asked for the code: a supplier's is re-requested from the supplier endpoint. */
    const response: OtpSendResponse = yield call(
      action.payload.audience === 'supplier' ? authService.sendSupplierOtp : authService.resendOtp,
      action.payload.email,
    );
    yield put(resendOtpSuccess(otpTimers(response)));
    logAuthEvent('otp.resent');
  } catch (error) {
    yield put(resendOtpFailure(getErrorMessage(normalizeError(error))));
  }
}

/**
 * A supplier's sign-in (2026-10-23): the code is checked by the supplier endpoint and the session's user is read from
 * the token it returns -- a supplier has no `GET /users/me` to call, and no organization to bootstrap.
 */
function* verifySupplierOtpSaga(action: ReturnType<typeof verifyOtpStart>): SagaIterator {
  let minted = false;
  try {
    const tokens: AuthTokens = yield call(authService.verifySupplierOtp, {
      email: action.payload.email,
      otp: action.payload.otp,
    });
    const expiresAt = persist(tokens);
    minted = true;
    const identity = supplierFromToken(tokens.accessToken);
    if (!identity) {
      throw createApiError({ message: AUTH_MESSAGES.UNEXPECTED_RESPONSE, code: 'SERVER_ERROR', status: 500 });
    }
    const user = supplierUser(identity);
    persistUser(user);
    yield put(
      verifyOtpSuccess({
        user,
        token: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        tokenType: tokens.tokenType,
        expiresAt,
        profileLoaded: true,
      }),
    );
    logAuthEvent('otp.verified');
  } catch (error) {
    if (minted) yield call(endSession);
    yield put(verifyOtpFailure(getErrorMessage(normalizeError(error))));
    logAuthEvent('otp.failure');
  }
}

function* verifyOtpSaga(action: ReturnType<typeof verifyOtpStart>): SagaIterator {
  if (action.payload.audience === 'supplier') {
    yield call(verifySupplierOtpSaga, action);
    return;
  }
  let minted = false;
  try {
    const tokens: AuthTokens = yield call(authService.verifyOtp, action.payload);
    const expiresAt = persist(tokens);
    minted = true;

    const { user, loaded } = yield call(resolveUser, action.payload.email);

    yield put(
      verifyOtpSuccess({
        user,
        token: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        tokenType: tokens.tokenType,
        expiresAt,
        profileLoaded: loaded,
      }),
    );
    logAuthEvent('otp.verified');
  } catch (error) {
    /*
      A wrong, expired or exhausted code is answered 401 ("Invalid OTP. Please enter a valid OTP.") by a
      public endpoint: a refused code, not an expired session. It is reported with the server's own
      message and nothing else happens -- in particular the session is NOT ended, because `logout()`
      resets the OTP state and the next Verify or Resend would go out with an empty email (which the
      server refused as "Request validation failed"). Only a token minted a moment ago and rejected by
      the very next call ends the session, as in loginSaga.
    */
    if (minted && isSessionExpiry(error)) yield call(endSession);
    yield put(
      verifyOtpFailure(
        isEntityInactiveSignIn(error) ? ENTITY_ACCESS_ERROR_MESSAGE : getErrorMessage(normalizeError(error)),
      ),
    );
    logAuthEvent('otp.failure');
  }
}

export function* authSaga(): SagaIterator {
  yield takeLatest(loginStart.type, loginSaga);
  yield takeLatest(logoutStart.type, logoutSaga);
  yield takeLatest(hydrateAuth.type, hydrateAuthSaga);
  yield takeLatest(sendOtpStart.type, sendOtpSaga);
  yield takeLatest(resendOtpStart.type, resendOtpSaga);
  yield takeLatest(verifyOtpStart.type, verifyOtpSaga);
}
